'use client';
import React, { useEffect, useState } from 'react';
import { useFilter, useTranslation } from '@/hook';
import Image from 'next/image';
import {
    CarStatus,
    CarStatusText,
    defaultEmiFilter,
    defaultPriceFilter,
    defaultYearFilter,
    FILTER_NAMES,
    FilterDisplayItemData,
    FilterTabsInterface,
    IClientRetailFilters,
    ISearchTerms,
    KILOMETERS,
    MakeFilterUiOject,
    TRANSMISSION,
} from '@/types';
import SwipeableEdgeDrawerAnchored from '../shared/bottom-sheet/bottom-sheet-anchored.component';
import Typography from '@/components/shared/typography/typography.component';
import { CloseButtonIcon, ButtonLoading } from 'public/Images';
import { FilterGroup, FilterTabItem } from '@/components/filtersV2/index';
import {
    cloneClientRetailFilters,
    convertClientToEsFilters,
    formatBrandName,
    formatPriceForDisplay,
    formatYearForDisplay,
    slugify,
    FILTER_YEARS,
} from '@/utils/filterUtil';
import { numberWithCommas } from '@/utils';
import { getCarListingsElastic } from '@/api-requests/car-listing-elastic.apis';
import useDebounce from '@/hook/debounce.hook';
import myImageLoader from '@/image.local.loader';
import Button from '../shared/Button/button-styled.component';
import BottomActions from '../shared/bottom-sheet/bottom-actions.component';
import { colors } from '@/constants';
import ColoredTag from '../shared/colored-tag/colored-tag.component';
import {
    CATEGORY_BADGE_CONFIG,
    getCategoryBadgeConfig,
} from '../car-details/CarProfileNew/constants';
import { CarTypesEnum } from '../car-details-v2/car-type';

interface TFiltersMobileProps {
    applyFiltersClicked: (filters: IClientRetailFilters) => void;
    clearFiltersClicked: () => void;
    open: boolean;
    setOpen: (flag: boolean) => void;
    filterSelected: string;
    displayedCount: number;
    searchTerms: ISearchTerms;
    localFilters: IClientRetailFilters;
}
const FiltersMobile = ({
    applyFiltersClicked,
    open,
    setOpen,
    filterSelected,
    localFilters,
    displayedCount,
    searchTerms,
}: TFiltersMobileProps) => {
    const { t, i18n } = useTranslation();
    const lang = i18n.language;
    const [tempFilters, setTempFilters] = useState(
        cloneClientRetailFilters(localFilters)
    );
    const [isCountLoading, setIsCountLoading] = useState(false);
    const [tempResCount, setTempResCount] = useState(displayedCount);
    const {
        clearFilters,
        makeModelValueChanged,
        kilometersValueChanged,
        bodyTypeValueChanged,
        transmissionValueChanged,
        localFilters: internalFilters,
        priceRangeChanged,
        discountedValueToggle,
        emiRangeChanged,
        yearRangeChanged,
        availableValueChanged,
        filterObjectNotEmpty,
        priceTagClicked,
        discountedTagClicked,
        emiTagClicked,
        makeTagClicked,
        yearTagClicked,
        modelTagClicked,
        bodyTypeTagClicked,
        availableTagClicked,
        kilometersTagClicked,
        transmissionTagClicked,
        categoryTagClicked,
        categoryValuedChanged,
    } = useFilter(searchTerms, tempFilters, setTempFilters);

    const updateTotalResCount = useDebounce(() => {
        setIsCountLoading(true);
        getCarListingsElastic({
            filters: convertClientToEsFilters(tempFilters, searchTerms),
        })
            .then(newCars => {
                if (newCars && 'total' in newCars) {
                    setTempResCount(newCars.total);
                }
            })
            .catch(err => {
                // eslint-disable-next-line no-console
                console.log(err);
            })
            .finally(() => {
                setIsCountLoading(false);
            });
    }, 300);

    useEffect(() => {
        updateTotalResCount();
    }, [tempFilters]);

    const yearMax = FILTER_YEARS[FILTER_YEARS.length - 1];
    const yearMin = FILTER_YEARS[0];

    const applyFilters = () => {
        applyFiltersClicked({ ...internalFilters });
    };

    const filterTabs: FilterTabsInterface[] = [
        {
            text: t(FILTER_NAMES.MAKE_MODEL),
            selected: true,
            id: FILTER_NAMES.MAKE_MODEL,
            count: 0,
        },
        {
            text: t(FILTER_NAMES.PRICE),
            selected: false,
            id: FILTER_NAMES.PRICE,
            count: 0,
        },
        {
            text: t(FILTER_NAMES.DISCOUNTED),
            selected: false,
            id: FILTER_NAMES.DISCOUNTED,
            count: 0,
        },
        {
            text: t('Refurbished Status'),
            selected: false,
            id: FILTER_NAMES.REFURBISHED,
            count: 0,
        },
        {
            text: t(FILTER_NAMES.EMI),
            selected: false,
            id: FILTER_NAMES.EMI,
            count: 0,
        },
        {
            text: t(FILTER_NAMES.AVAILABLE),
            selected: false,
            id: FILTER_NAMES.AVAILABLE,
            count: 0,
        },
        {
            text: t(FILTER_NAMES.MODEL_YEAR),
            selected: false,
            id: FILTER_NAMES.MODEL_YEAR,
            count: 0,
        },

        {
            text: t(FILTER_NAMES.KILOMETERS),
            selected: false,
            id: FILTER_NAMES.KILOMETERS,
            count: 0,
        },
        {
            text: t(FILTER_NAMES.TRANSMISSION),
            selected: false,
            id: FILTER_NAMES.TRANSMISSION,
            count: 0,
        },
        {
            text: t(FILTER_NAMES.BODY_STYLE),
            selected: false,
            id: FILTER_NAMES.BODY_STYLE,
            count: 0,
        },
    ];

    const [filterTabsState, setFilterTabsState] =
        useState<FilterTabsInterface[]>(filterTabs);

    useEffect(() => {
        tabClicked(filterSelected);
    }, [filterSelected, open]);

    const badgeConfig =
        tempFilters.category !== 'all' &&
        getCategoryBadgeConfig(tempFilters.category, t);

    const tabClicked = (id: string | number) => {
        const modifiedTabs = filterTabsState.map(item => {
            return { ...item, selected: item.text === id || item.id === id };
        });
        setFilterTabsState(modifiedTabs);
    };

    const handleCategoryFilterClick = (value: string | undefined) => {
        if (value === undefined || value === 'undefined') {
            categoryValuedChanged('all');
        } else {
            categoryValuedChanged(value as CarTypesEnum);
        }
    };
    return (
        <SwipeableEdgeDrawerAnchored
            open={open}
            setOpen={setOpen}
            key={`mobile-filter-${open}`}
            horizontalPadding={0}
        >
            <div className={'h-[83vh] relative'}>
                <div
                    className={'mb-3.5 flex items-center justify-between px-4'}
                >
                    <Typography
                        className={'text-gray-800'}
                        variant={'subtitle2SemiBold'}
                    >
                        {t('Filters')}
                    </Typography>
                    <Image
                        onClick={() => {
                            setOpen(false);
                        }}
                        loading={'lazy'}
                        width={CloseButtonIcon.width * 1.5}
                        height={CloseButtonIcon.height * 1.5}
                        src={CloseButtonIcon.src}
                        alt="close"
                    />
                </div>
                {filterObjectNotEmpty && (
                    <div className="bg-white w-full px-4">
                        <div className="flex gap-2 overflow-x-scroll scrollbar-hide -mx-4 px-3 pb-4">
                            {tempFilters.price.min !==
                                defaultPriceFilter.min && (
                                <ColoredTag
                                    text={formatPriceForDisplay(
                                        [
                                            Math.max(
                                                tempFilters.price.min,
                                                searchTerms.priceTerms.min
                                            ),
                                            Math.min(
                                                tempFilters.price.max,
                                                searchTerms.priceTerms.max
                                            ),
                                        ],
                                        lang,
                                        t
                                    )}
                                    tagClicked={priceTagClicked}
                                />
                            )}
                            {tempFilters.discounted && (
                                <ColoredTag
                                    text="Discounted"
                                    tagClicked={discountedTagClicked}
                                />
                            )}
                            {tempFilters.category !== undefined &&
                                tempFilters.category !== 'all' && (
                                    <ColoredTag
                                        text={badgeConfig.title}
                                        tagClicked={categoryTagClicked}
                                    />
                                )}
                            {tempFilters.emi.min !== defaultEmiFilter.min && (
                                <ColoredTag
                                    text={formatPriceForDisplay(
                                        [
                                            Math.max(
                                                tempFilters.emi.min,
                                                searchTerms.emiTerms.min
                                            ),
                                            Math.min(
                                                tempFilters.emi.max,
                                                searchTerms.emiTerms.max
                                            ),
                                        ],
                                        lang,
                                        t
                                    )}
                                    tagClicked={emiTagClicked}
                                />
                            )}
                            {Object.keys(tempFilters.makeModel).map(make => {
                                return Object.keys(
                                    tempFilters.makeModel[make].models
                                ).length ===
                                    searchTerms.makeModelTree[make].modelsSlug
                                        .length ? (
                                    <ColoredTag
                                        key={make}
                                        text={`${formatBrandName(
                                            searchTerms.makeModelTree[make].make
                                        )} - ${t('any')}`}
                                        tagClicked={makeTagClicked}
                                    />
                                ) : (
                                    Object.keys(
                                        tempFilters.makeModel[make].models
                                    ).map(model => (
                                        <div
                                            onClick={() => {
                                                modelTagClicked({
                                                    make,
                                                    model,
                                                });
                                            }}
                                            key={model}
                                        >
                                            <ColoredTag
                                                text={`${formatBrandName(
                                                    searchTerms.makeModelTree[
                                                        make
                                                    ].make
                                                )} - ${formatBrandName(
                                                    searchTerms.makeModelTree[
                                                        make
                                                    ].models[model]
                                                )}`}
                                                tagClicked={() =>
                                                    modelTagClicked({
                                                        make,
                                                        model,
                                                    })
                                                }
                                            />
                                        </div>
                                    ))
                                );
                            })}

                            {Object.keys(tempFilters.bodystyle).length !== 0 &&
                                Object.keys(tempFilters.bodystyle).map(item => {
                                    return (
                                        <ColoredTag
                                            key={item}
                                            text={
                                                searchTerms.bodyStyleTerms
                                                    .bodyStyles[item]
                                            }
                                            tagClicked={bodyTypeTagClicked}
                                        />
                                    );
                                })}
                            {Object.keys(tempFilters.transmission).length !==
                                0 &&
                                Object.keys(tempFilters.transmission).map(
                                    item => (
                                        <ColoredTag
                                            key={item}
                                            text={item}
                                            tagClicked={transmissionTagClicked}
                                        />
                                    )
                                )}

                            {tempFilters.kilometers &&
                                tempFilters.kilometers !== 0 && (
                                    <ColoredTag
                                        text={`${numberWithCommas(
                                            tempFilters.kilometers
                                        )} ${t('km')} ${t('& Less')}`}
                                        tagClicked={kilometersTagClicked}
                                    />
                                )}
                            {tempFilters.status && (
                                <ColoredTag
                                    text={
                                        t(CarStatusText[tempFilters.status]) ||
                                        ''
                                    }
                                    tagClicked={availableTagClicked}
                                />
                            )}
                            {tempFilters.year.min !== defaultYearFilter.min && (
                                <ColoredTag
                                    text={formatYearForDisplay(
                                        tempFilters.year.max !==
                                            defaultYearFilter.max
                                            ? [
                                                  tempFilters.year.min,
                                                  tempFilters.year.max,
                                              ]
                                            : [tempFilters.year.min]
                                    )}
                                    tagClicked={yearTagClicked}
                                />
                            )}
                        </div>
                    </div>
                )}

                <div
                    className="flex gap-3"
                    style={{ paddingInlineEnd: '1rem' }}
                >
                    <div className="w-2/5">
                        {filterTabsState?.map(item => (
                            <div
                                key={`${item.id}-${item.selected}`}
                                className="mb-2"
                            >
                                <FilterTabItem
                                    text={item.text}
                                    selected={item.selected}
                                    id={item.id}
                                    tabClicked={tabClicked}
                                    count={item.count}
                                />
                            </div>
                        ))}
                    </div>
                    <div className="w-3/5 pb-20">
                        {filterTabsState[0].selected && (
                            <FilterGroup
                                label={t('Make & Model')}
                                data={Object.keys(
                                    searchTerms.makeModelTree
                                ).map(make => {
                                    return {
                                        text: formatBrandName(make),
                                        value: make,
                                        models: Object.keys(
                                            searchTerms.makeModelTree[make]
                                                .models
                                        ).map(
                                            model =>
                                                ({
                                                    text: searchTerms
                                                        .makeModelTree[make]
                                                        .models?.[model],
                                                    value: model,
                                                    selected: !!(
                                                        tempFilters.makeModel[
                                                            make
                                                        ]?.models?.[model] ||
                                                        false
                                                    ),
                                                } as FilterDisplayItemData)
                                        ),
                                        selected: !!tempFilters.makeModel[make],
                                    } as MakeFilterUiOject;
                                })}
                                filterType="multiSelect"
                                filterItemClicked={() => null}
                                makeModelClicked={makeModelValueChanged}
                            />
                        )}
                        {filterTabsState[1].selected && (
                            <FilterGroup
                                key={`price-${tempFilters.price.min}-${tempFilters.price.max}`}
                                label={t('price')}
                                data={{
                                    min: searchTerms.priceTerms.min,
                                    max: searchTerms.priceTerms.max,
                                }}
                                range={[
                                    Math.max(
                                        tempFilters.price.min,
                                        searchTerms.priceTerms.min
                                    ),
                                    Math.min(
                                        tempFilters.price.max,
                                        searchTerms.priceTerms.max
                                    ),
                                ]}
                                filterType="slider"
                                rangeChanged={priceRangeChanged}
                                formatDisplayedValue={v => {
                                    const formatedNumber = numberWithCommas(v);
                                    return `${t('EGP')} ${formatedNumber}`;
                                }}
                                vertical={true}
                            />
                        )}

                        {filterTabsState[2].selected && (
                            <label className="relative flex justify-between items-center text-xl mt-3">
                                <Typography variant={'body2Medium'}>
                                    {t('discountedFilterLabel')}
                                </Typography>
                                <input
                                    type="checkbox"
                                    onChange={discountedValueToggle}
                                    checked={tempFilters.discounted}
                                    className="absolute left-1/2 -translate-x-1/2 w-full h-full peer appearance-none rounded-md"
                                />
                                <span
                                    className="w-10 h-6 flex items-center flex-shrink-0 ml-2 p-0.5 bg-gray-300 rounded-full duration-300
                    ease-in-out peer-checked:bg-primary
                    after:w-5 after:h-5
                    after:bg-white after:rounded-full
                    after:shadow-md after:duration-300
                    en:peer-checked:after:translate-x-4 ar:peer-checked:after:-translate-x-4"
                                ></span>
                            </label>
                        )}
                        {filterTabsState[3].selected && (
                            <FilterGroup
                                label={t('Refurbished Status')}
                                data={[
                                    {
                                        text: t('All Cars'),
                                        value: undefined,
                                        description: t('AllCarsDesc'),
                                        selected:
                                            tempFilters.category === 'all' ||
                                            tempFilters.category === undefined,
                                    },
                                    ...searchTerms.categoryTerms.categoryRaw
                                        .sort((a, b) => {
                                            const order = [
                                                CarTypesEnum.CERTIFIED,
                                                CarTypesEnum.LITE,
                                                CarTypesEnum.Dealer,
                                            ];
                                            return (
                                                order.indexOf(a) -
                                                order.indexOf(b)
                                            );
                                        })
                                        .map(cat => ({
                                            text: t(
                                                CATEGORY_BADGE_CONFIG[cat].title
                                            ),
                                            icon: CATEGORY_BADGE_CONFIG[cat]
                                                .filterIcon,
                                            description: t(
                                                CATEGORY_BADGE_CONFIG[cat].desc
                                            ),
                                            selected:
                                                tempFilters.category === cat,
                                            value: cat,
                                            isNew: CATEGORY_BADGE_CONFIG[cat]
                                                .isNew,
                                        })),
                                ]}
                                filterType="radiobox"
                                filterItemClicked={handleCategoryFilterClick}
                            />
                        )}

                        {filterTabsState[4].selected && (
                            <FilterGroup
                                key={`emi-${tempFilters.emi.min}-${tempFilters.emi.max}`}
                                label={t('emi')}
                                data={{
                                    min: searchTerms.emiTerms.min,
                                    max: searchTerms.emiTerms.max,
                                }}
                                range={[
                                    Math.max(
                                        tempFilters.emi.min,
                                        searchTerms.emiTerms.min
                                    ),
                                    Math.min(
                                        tempFilters.emi.max,
                                        searchTerms.emiTerms.max
                                    ),
                                ]}
                                filterType="slider"
                                rangeChanged={emiRangeChanged}
                                formatDisplayedValue={v => {
                                    const formatedNumber = numberWithCommas(v);
                                    return `${t('EGP')} ${formatedNumber}`;
                                }}
                                vertical={true}
                            />
                        )}
                        {filterTabsState[5].selected && (
                            <FilterGroup
                                label={t('Car Status')}
                                data={Object.values(CarStatus).map(cs => {
                                    return {
                                        text: t(CarStatusText[cs]),
                                        value: cs,
                                        selected:
                                            tempFilters.status &&
                                            slugify(tempFilters.status) ===
                                                slugify(cs),
                                    } as FilterDisplayItemData;
                                })}
                                filterType="radiobox"
                                filterItemClicked={availableValueChanged}
                            />
                        )}
                        {filterTabsState[6].selected && (
                            <FilterGroup
                                label={t('Year')}
                                filterType="slider"
                                data={{
                                    min: yearMin,
                                    max: yearMax,
                                }}
                                vertical
                                rangeChanged={yearRangeChanged}
                                range={[
                                    Math.max(
                                        tempFilters.year.min,
                                        FILTER_YEARS[0]
                                    ),
                                    Math.min(
                                        tempFilters.year.max,
                                        FILTER_YEARS[FILTER_YEARS.length - 1]
                                    ),
                                ]}
                                formatDisplayedValue={v => `${v}`}
                            />
                        )}
                        {filterTabsState[7].selected && (
                            <FilterGroup
                                label={t('kilometers')}
                                data={Object.values(KILOMETERS).map(km => {
                                    return {
                                        text: `${numberWithCommas(km)} ${t(
                                            'KM & Less'
                                        )}`,
                                        value: km,
                                        selected:
                                            tempFilters.kilometers ===
                                            Number(km),
                                    } as FilterDisplayItemData;
                                })}
                                filterType="radiobox"
                                filterItemClicked={kilometersValueChanged}
                            />
                        )}
                        {filterTabsState[8].selected && (
                            <FilterGroup
                                label={t('Transmission')}
                                data={Object.values(TRANSMISSION).map(t => {
                                    return {
                                        text: t,
                                        value: t,
                                        selected: !!tempFilters.transmission[t],
                                    } as FilterDisplayItemData;
                                })}
                                filterType="checkbox"
                                filterItemClicked={transmissionValueChanged}
                            />
                        )}
                        {filterTabsState[9].selected && (
                            <FilterGroup
                                label={t('Body Style')}
                                data={searchTerms.bodyStyleTerms.bodyStylesRaw.map(
                                    bt => {
                                        return {
                                            text: bt,
                                            value: bt,
                                            selected:
                                                !!tempFilters.bodystyle[bt],
                                        } as FilterDisplayItemData;
                                    }
                                )}
                                filterType="checkbox"
                                filterItemClicked={bodyTypeValueChanged}
                            />
                        )}
                    </div>
                </div>

                <BottomActions contentClassName="gap-2">
                    <Button
                        onClick={() => applyFilters()}
                        disabled={
                            tempResCount === 0 ||
                            isCountLoading ||
                            !filterObjectNotEmpty
                        }
                        variant="primary"
                        className={`flex-[3] ${
                            isCountLoading ? 'bg-primary-100' : ''
                        }`}
                    >
                        <div className="px-4 py-3 min-h-4 flex items-center justify-center">
                            {isCountLoading ? (
                                <div className="animate-spin flex items-center justify-center">
                                    <img
                                        src={myImageLoader({
                                            src: ButtonLoading.src,
                                        })}
                                        alt={'Count Loading'}
                                        width={16}
                                        height={16}
                                    />
                                </div>
                            ) : (
                                <Typography
                                    variant={'body2SemiBold'}
                                    className="text-center w-full"
                                >
                                    {t('displayCarsCount', {
                                        value: tempResCount,
                                    })}
                                </Typography>
                            )}
                        </div>
                    </Button>
                    <Button
                        onClick={clearFilters}
                        variant={'outlined'}
                        disabled={!filterObjectNotEmpty}
                        style={{
                            color: !filterObjectNotEmpty
                                ? colors['gray-400']
                                : colors['error-600'],
                        }}
                        className="flex-1 whitespace-nowrap disabled:bg-white"
                    >
                        <Typography
                            variant="body2SemiBold"
                            className="px-4 py-3 !text-inherit"
                        >
                            {t('Clear')}
                        </Typography>
                    </Button>
                </BottomActions>
            </div>
        </SwipeableEdgeDrawerAnchored>
    );
};

export default FiltersMobile;
